import { logger } from './index';

interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  metaKey?: boolean;
  description: string;
  action: () => void;
}

class KeyboardShortcutManager {
  private shortcuts: KeyboardShortcut[] = [];
  private isEnabled = true;

  constructor() {
    this.setupDefaultShortcuts();
    this.setupEventListeners();
  }

  private setupDefaultShortcuts() {
    this.addShortcut({
      key: 'J',
      ctrlKey: true,
      shiftKey: true,
      description: 'Clear all logs',
      action: () => {
        this.showClearConfirmation();
      },
    });

    this.addShortcut({
      key: 'S',
      ctrlKey: true,
      shiftKey: true,
      description: 'Show logs statistics',
      action: () => {
        this.showStats();
      },
    });

    this.addShortcut({
      key: 'H',
      ctrlKey: true,
      shiftKey: true,
      description: 'Show keyboard shortcuts help',
      action: () => {
        this.showHelp();
      },
    });

    this.addShortcut({
      key: 'E',
      ctrlKey: true,
      shiftKey: true,
      description: 'Export logs to JSON file',
      action: () => {
        this.exportToJSON();
      },
    });
  }

  private setupEventListeners() {
    document.addEventListener('keydown', e => {
      if (!this.isEnabled) {
        return;
      }

      // Ignore if user is typing
      if (this.isUserTyping(e)) {
        return;
      }

      const shortcut = this.findMatchingShortcut(e);
      if (shortcut) {
        e.preventDefault();
        e.stopPropagation();
        shortcut.action();
      }
    });
  }

  private isUserTyping(e: KeyboardEvent): boolean {
    const target = e.target as HTMLElement;
    if (!target) {
      return false;
    }

    const tagName = target.tagName.toLowerCase();
    const isInput = tagName === 'input' || tagName === 'textarea';
    const isContentEditable = target.contentEditable === 'true';
    const isSelect = tagName === 'select';

    return isInput || isContentEditable || isSelect;
  }

  private findMatchingShortcut(e: KeyboardEvent): KeyboardShortcut | undefined {
    return this.shortcuts.find(shortcut => {
      return (
        shortcut.key === e.key &&
        !!shortcut.ctrlKey === e.ctrlKey &&
        !!shortcut.shiftKey === e.shiftKey &&
        !!shortcut.altKey === e.altKey &&
        !!shortcut.metaKey === e.metaKey
      );
    });
  }

  addShortcut(shortcut: KeyboardShortcut) {
    this.shortcuts.push(shortcut);
  }

  removeShortcut(key: string, ctrlKey = false, shiftKey = false) {
    this.shortcuts = this.shortcuts.filter(
      s => !(s.key === key && s.ctrlKey === ctrlKey && s.shiftKey === shiftKey)
    );
  }

  enable() {
    this.isEnabled = true;
  }

  disable() {
    this.isEnabled = false;
  }

  private showClearConfirmation() {
    const confirmed = window.confirm(
      '⚠️ CLEAR ALL LOGS\n\n' +
        'Are you sure you want to clear all logs?\n\n' +
        'This action cannot be undone.\n' +
        'All data will be lost permanently.'
    );

    if (confirmed) {
      logger.clear();
      this.showNotification('All logs have been cleared.', 'info');
    }
  }

  private showStats() {
    const stats = logger.getStats();

    const message = [
      '📊 LOGGER STATISTICS',
      '',
      `Total logs: ${stats.total}`,
      '',
      'By level:',
      ...Object.entries(stats.byLevel).map(([level, count]) => `  ${level}: ${count}`),
      '',
      'By group:',
      ...Object.entries(stats.byGroup).map(([group, count]) => `  ${group}: ${count}`),
      '',
      `Storage size: ${(stats.storageSize / 1024 / 1024).toFixed(2)} MB`,
      '',
      'Recent errors:',
      ...stats.recentErrors.map(error => `  ${error.datetime}: ${error.message}`),
    ].join('\n');

    alert(message);
  }

  private showHelp() {
    const helpText = [
      '⌨️ KEYBOARD SHORTCUTS',
      '',
      ...this.shortcuts.map(shortcut => {
        const keys = [
          shortcut.ctrlKey && 'Ctrl',
          shortcut.shiftKey && 'Shift',
          shortcut.altKey && 'Alt',
          shortcut.metaKey && 'Cmd',
          shortcut.key,
        ]
          .filter(Boolean)
          .join('+');

        return `${keys}: ${shortcut.description}`;
      }),
      '',
      '💡 TIP: These shortcuts work globally when not typing in input fields.',
    ].join('\n');

    alert(helpText);
  }

  private exportToJSON() {
    try {
      const data = {
        timestamp: new Date().toISOString(),
        logs: logger.search({}),
        stats: logger.getStats(),
        summary: {
          total: logger.search({}).length,
          byLevel: logger.search({}).reduce(
            (acc, log) => {
              acc[log.level] = (acc[log.level] || 0) + 1;
              return acc;
            },
            {} as Record<string, number>
          ),
        },
      };

      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `app-logs-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);

      this.showNotification('Logs exported to JSON successfully!', 'success');
    } catch (error) {
      this.showNotification('Failed to export logs: ' + (error as Error).message, 'error');
    }
  }

  private showNotification(message: string, type: 'success' | 'error' | 'info' | 'warning') {
    // Create simple notification
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 12px 20px;
      border-radius: 6px;
      color: white;
      font-family: Arial, sans-serif;
      font-size: 14px;
      z-index: 10000;
      max-width: 300px;
      word-wrap: break-word;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      animation: slideIn 0.3s ease-out;
    `;

    // Colors for different types
    const colors = {
      success: '#4caf50',
      error: '#f44336',
      info: '#2196f3',
      warning: '#ff9800',
    };

    notification.style.backgroundColor = colors[type];
    notification.textContent = message;

    // Animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(notification);

    // Auto remove after 3 seconds
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease-in';
      setTimeout(() => {
        document.body.removeChild(notification);
        document.head.removeChild(style);
      }, 300);
    }, 3000);

    // Add exit animation
    const slideOutStyle = document.createElement('style');
    slideOutStyle.textContent = `
      @keyframes slideOut {
        from { transform: translateX(0); opacity: 1; }
        to { transform: translateX(100%); opacity: 0; }
      }
    `;
    document.head.appendChild(slideOutStyle);
  }

  // Method to get all available shortcuts
  getShortcuts(): KeyboardShortcut[] {
    return [...this.shortcuts];
  }

  // Method to check if shortcuts are enabled
  isShortcutsEnabled(): boolean {
    return this.isEnabled;
  }
}

export const keyboardManager = new KeyboardShortcutManager();
export { KeyboardShortcutManager };
export type { KeyboardShortcut };
